import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Activity, Calendar, Phone, Plus, AlertTriangle, CheckCircle, ChevronRight, Heart, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { useAuth } from '../../store/authStore';
import { getSessions, getReadings } from '../../api/sessions';
import { getAlerts } from '../../api/alerts';
import { getAppointments } from '../../api/appointments';
import { SkeletonBlock } from '../../components/SkeletonBlock';
import { Button } from '../../components/Button';
import { InputField } from '../../components/InputField';
import { LaborPhaseEstimator } from './LaborPhaseEstimator';
const SYMPTOMS = ['Nausea', 'Fatigue', 'Headache', 'Swelling', 'Heartburn', 'Back Pain', 'Spotting'];
export function MotherScreen() {
  const navigate = useNavigate();
  const {
    user,
    token
  } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [sessionData, setSessionData] = useState<any>(null);
  const [piezoReadings, setPiezoReadings] = useState<any[]>([]);
  const [alerts, setAlerts] = useState<any[]>([]);
  const [nextAppointment, setNextAppointment] = useState<any>(null);
  // Local Storage States
  const [weightLog, setWeightLog] = useState<any[]>(() => {
    const saved = localStorage.getItem('aurora_weight_log');
    return saved ? JSON.parse(saved) : [];
  });
  const [bpLog, setBpLog] = useState<any[]>(() => {
    const saved = localStorage.getItem('aurora_bp_log');
    return saved ? JSON.parse(saved) : [];
  });
  const [mhrLog, setMhrLog] = useState<any[]>(() => {
    const saved = localStorage.getItem('aurora_mhr_log');
    return saved ? JSON.parse(saved) : [];
  });
  const [symptomLog, setSymptomLog] = useState<any[]>(() => {
    const saved = localStorage.getItem('aurora_symptom_log');
    return saved ? JSON.parse(saved) : [];
  });
  // Bottom Sheet States
  const [showWeightSheet, setShowWeightSheet] = useState(false);
  const [weightInput, setWeightInput] = useState('');
  const [weightDate, setWeightDate] = useState(new Date().toISOString().split('T')[0]);
  const [showBpSheet, setShowBpSheet] = useState(false);
  const [bpSysInput, setBpSysInput] = useState('');
  const [bpDiaInput, setBpDiaInput] = useState('');
  const [bpDate, setBpDate] = useState(new Date().toISOString().split('T')[0]);
  const [showMhrSheet, setShowMhrSheet] = useState(false);
  const [mhrInput, setMhrInput] = useState('');
  const [mhrDate, setMhrDate] = useState(new Date().toISOString().split('T')[0]);
  const [mhrTime, setMhrTime] = useState(() => {
    const d = new Date();
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  });
  useEffect(() => {
    const fetchData = async () => {
      if (!token) return;
      try {
        const [sessionsRes, alertsRes, apptsRes] = await Promise.all([getSessions(token), getAlerts(token), getAppointments(token)]);
        if (sessionsRes && sessionsRes.length > 0) {
          const latestSession = sessionsRes[0];
          setSessionData(latestSession);
          if (latestSession.id) {
            const readingsRes = await getReadings(token, latestSession.id, {
              sensorType: 'piezo',
              limit: 100
            });
            if (readingsRes && readingsRes.data) {
              setPiezoReadings(readingsRes.data.reverse());
            }
          }
        }
        if (alertsRes) {
          setAlerts(alertsRes);
        }
        if (apptsRes && apptsRes.length > 0) {
          const upcoming = apptsRes.filter((a: any) => new Date(a.appointmentDate).getTime() >= new Date().getTime()).sort((a: any, b: any) => new Date(a.appointmentDate).getTime() - new Date(b.appointmentDate).getTime());
          if (upcoming.length > 0) {
            setNextAppointment(upcoming[0]);
          }
        }
      } catch (err) {
        // Handle silently
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [token]);
  // Derived Data
  const pregnancyWeek = 24; // Hardcoded for demo, could be derived from user.dueDate
  const trimester = pregnancyWeek < 14 ? '1st' : pregnancyWeek < 28 ? '2nd' : '3rd';
  const hasRecentAlerts = alerts.some(a => a.status === 'active' || a.status === 'resolved' && Date.now() - new Date(a.createdAt).getTime() < 86400000 * 3);
  const contractionData = useMemo(() => {
    if (!piezoReadings.length) return [];
    return piezoReadings.map((r: any, i: number) => ({
      time: new Date(r.timestamp).toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit'
      }),
      value: r.value
    }));
  }, [piezoReadings]);
  const lastPeakTime = useMemo(() => {
    if (!piezoReadings.length) return null;
    const peaks = piezoReadings.filter(r => r.value > 2500);
    if (!peaks.length) return null;
    const lastPeak = peaks[peaks.length - 1];
    const diffMins = Math.floor((Date.now() - new Date(lastPeak.timestamp).getTime()) / 60000);
    return diffMins;
  }, [piezoReadings]);
  const weightChartData = useMemo(() => {
    return [...weightLog].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()).map(entry => ({
      date: new Date(entry.date).toLocaleDateString([], {
        month: 'short',
        day: 'numeric'
      }),
      weight: entry.weight
    }));
  }, [weightLog]);
  const mhrChartData = useMemo(() => {
    return [...mhrLog].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()).slice(-30).map(entry => ({
      time: new Date(entry.timestamp).toLocaleDateString([], {
        month: 'short',
        day: 'numeric'
      }),
      bpm: entry.bpm
    }));
  }, [mhrLog]);
  const latestMhr = useMemo(() => {
    if (!mhrLog.length) return null;
    return [...mhrLog].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())[0];
  }, [mhrLog]);
  const getMhrStatus = (bpm: number) => {
    if (bpm < 60 || bpm > 130) return {
      label: 'High',
      color: 'text-danger',
      bg: 'bg-danger/10',
      dot: 'bg-danger'
    };
    if (bpm > 100) return {
      label: 'Elevated',
      color: 'text-amber-600',
      bg: 'bg-amber-500/15',
      dot: 'bg-amber-500'
    };
    return {
      label: 'Normal',
      color: 'text-accent',
      bg: 'bg-accent/15',
      dot: 'bg-accent'
    };
  };
  const todaySymptoms = useMemo(() => {
    const todayStr = new Date().toDateString();
    return symptomLog.filter(s => new Date(s.timestamp).toDateString() === todayStr);
  }, [symptomLog]);
  // Handlers
  const handleLogSymptom = (symptom: string) => {
    const newLog = [{
      symptom,
      timestamp: new Date().toISOString()
    }, ...symptomLog];
    setSymptomLog(newLog);
    localStorage.setItem('aurora_symptom_log', JSON.stringify(newLog));
  };
  const handleSaveWeight = () => {
    if (!weightInput || !weightDate) return;
    const newEntry = {
      id: Date.now().toString(),
      weight: parseFloat(weightInput),
      date: weightDate
    };
    const newLog = [newEntry, ...weightLog];
    setWeightLog(newLog);
    localStorage.setItem('aurora_weight_log', JSON.stringify(newLog));
    setShowWeightSheet(false);
    setWeightInput('');
  };
  const handleSaveBp = () => {
    if (!bpSysInput || !bpDiaInput || !bpDate) return;
    const newEntry = {
      id: Date.now().toString(),
      systolic: parseInt(bpSysInput, 10),
      diastolic: parseInt(bpDiaInput, 10),
      date: bpDate
    };
    const newLog = [newEntry, ...bpLog];
    setBpLog(newLog);
    localStorage.setItem('aurora_bp_log', JSON.stringify(newLog));
    setShowBpSheet(false);
    setBpSysInput('');
    setBpDiaInput('');
  };
  const handleSaveMhr = () => {
    const bpmVal = parseInt(mhrInput, 10);
    if (!bpmVal || bpmVal < 30 || bpmVal > 220 || !mhrDate || !mhrTime) return;
    const timestamp = new Date(`${mhrDate}T${mhrTime}:00`).toISOString();
    const newEntry = {
      id: Date.now().toString(),
      bpm: bpmVal,
      timestamp
    };
    const newLog = [newEntry, ...mhrLog];
    setMhrLog(newLog);
    localStorage.setItem('aurora_mhr_log', JSON.stringify(newLog));
    setShowMhrSheet(false);
    setMhrInput('');
  };
  return <main className="w-full max-w-[800px] mx-auto pb-20 md:pb-0 animate-fadeInUp space-y-6" data-id="element-1591">
      {/* 1. HEADER CARD */}
      <div className="bg-white rounded-[24px] p-6 shadow-card border border-border/50 relative overflow-hidden" data-id="element-1592">
        <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-bl-full -z-0" data-id="element-1593" />
        <div className="relative z-10 flex justify-between items-start mb-6" data-id="element-1594">
          <div data-id="element-1595">
            <h1 className="text-[24px] font-semibold text-text-primary mb-1" data-id="element-1596">
              {user?.fullName || 'Mother'}
            </h1>
            <p className="text-[15px] text-text-secondary" data-id="element-1597">
              Week {pregnancyWeek} · {trimester} Trimester
            </p>
          </div>
          <div className={`px-3 py-1.5 rounded-pill text-[13px] font-medium flex items-center ${hasRecentAlerts ? 'bg-danger/10 text-danger' : 'bg-success/10 text-success'}`} data-id="element-1598">
            {hasRecentAlerts ? <>
                <AlertTriangle size={14} className="mr-1.5" data-id="element-1599" />
                Needs Attention
              </> : <>
                <CheckCircle size={14} className="mr-1.5" data-id="element-1600" />
                Healthy
              </>}
          </div>
        </div>

        <div className="space-y-2" data-id="element-1601">
          <div className="flex justify-between text-[12px] text-text-secondary font-medium px-1" data-id="element-1602">
            <span data-id="element-1603">Week 1</span>
            <span data-id="element-1604">Week 40</span>
          </div>
          <div className="w-full h-3 bg-secondary rounded-full overflow-hidden" data-id="element-1605">
            <motion.div initial={{
            width: 0
          }} animate={{
            width: `${pregnancyWeek / 40 * 100}%`
          }} transition={{
            duration: 1,
            ease: 'easeOut'
          }} className="h-full bg-primary rounded-full" data-id="element-1606" />
          </div>
        </div>
      </div>

      {/* 2. CONTRACTION TRACKER */}
      <div className="bg-white rounded-[24px] p-6 shadow-card border border-border/50" data-id="element-1607">
        <div className="flex items-center justify-between mb-6" data-id="element-1608">
          <div className="flex items-center" data-id="element-1609">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center mr-3" data-id="element-1610">
              <Activity size={20} className="text-primary" data-id="element-1611" />
            </div>
            <h2 className="text-[18px] font-semibold text-text-primary" data-id="element-1612">
              Contraction Tracker
            </h2>
          </div>
        </div>

        {isLoading ? <SkeletonBlock height="160px" borderRadius="16px" data-id="element-1613" /> : contractionData.length > 0 ? <>
            <div className="h-[160px] w-full mb-4" data-id="element-1614">
              <ResponsiveContainer width="100%" height="100%" data-id="element-1615">
                <LineChart data={contractionData} data-id="element-1616">
                  <XAxis dataKey="time" hide data-id="element-1617" />
                  <YAxis hide domain={['dataMin - 100', 'dataMax + 100']} data-id="element-1618" />
                  <Tooltip contentStyle={{
                borderRadius: '12px',
                border: 'none',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
              }} data-id="element-1619" />
                  <Line type="monotone" dataKey="value" stroke="#6B21E8" strokeWidth={3} dot={false} activeDot={{
                r: 6,
                fill: '#6B21E8'
              }} data-id="element-1620" />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <p className="text-[14px] text-text-secondary text-center" data-id="element-1621">
              {lastPeakTime !== null ? `Last peak: ${lastPeakTime}m ago` : 'No significant peaks detected recently'}
            </p>
          </> : <div className="h-[160px] bg-secondary rounded-[16px] flex flex-col items-center justify-center text-center p-4" data-id="element-1622">
            <Activity size={32} className="text-text-secondary/50 mb-2" data-id="element-1623" />
            <p className="text-[15px] font-medium text-text-primary mb-1" data-id="element-1624">
              No recent data
            </p>
            <p className="text-[13px] text-text-secondary max-w-[200px]" data-id="element-1625">
              Start a monitoring session to track contractions.
            </p>
          </div>}
      </div>

      {/* 3. LABOR PHASE & DILATION ESTIMATOR */}
      <LaborPhaseEstimator token={token} data-id="element-1626" />

      {/* 3.5 MATERNAL HEART RATE */}
      <div className="bg-white rounded-[24px] p-6 shadow-card border border-border/50" data-id="element-1627">
        <div className="flex items-center justify-between mb-5" data-id="element-1628">
          <div className="flex items-center" data-id="element-1629">
            <div className="w-10 h-10 rounded-full bg-accent/15 flex items-center justify-center mr-3" data-id="element-1630">
              <Heart size={20} className="text-accent" data-id="element-1631" />
            </div>
            <h2 className="text-[18px] font-semibold text-text-primary" data-id="element-1632">
              Maternal Heart Rate
            </h2>
          </div>
          <button onClick={() => setShowMhrSheet(true)} className="text-primary bg-primary/10 p-2 rounded-full hover:bg-primary/20 transition-colors" aria-label="Log heart rate" data-id="element-1633">
            <Plus size={18} data-id="element-1634" />
          </button>
        </div>

        {latestMhr ? <>
            <div className="flex items-end justify-between mb-5" data-id="element-1635">
              <div data-id="element-1636">
                <div className="flex items-baseline" data-id="element-1637">
                  <motion.span key={latestMhr.id} initial={{
                scale: 1.1,
                opacity: 0
              }} animate={{
                scale: 1,
                opacity: 1
              }} className="text-[44px] font-bold text-text-primary leading-none" data-id="element-1638">
                    {latestMhr.bpm}
                  </motion.span>
                  <span className="text-[14px] text-text-secondary font-medium ml-2 pb-1 tracking-wider uppercase" data-id="element-1639">
                    bpm
                  </span>
                </div>
                <p className="text-[12px] text-text-secondary mt-2" data-id="element-1640">
                  Last reading:{' '}
                  {new Date(latestMhr.timestamp).toLocaleDateString([], {
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
                </p>
              </div>
              {(() => {
            const status = getMhrStatus(latestMhr.bpm);
            return <span className={`inline-flex items-center px-3 py-1.5 rounded-pill text-[13px] font-semibold ${status.bg} ${status.color}`} data-id="element-1641">
                    <span className={`w-1.5 h-1.5 rounded-full ${status.dot} mr-2`} data-id="element-1642" />
                    {status.label}
                  </span>;
          })()}
            </div>

            {mhrChartData.length > 1 ? <div className="h-[100px] w-full" data-id="element-1643">
                <ResponsiveContainer width="100%" height="100%" data-id="element-1644">
                  <LineChart data={mhrChartData} data-id="element-1645">
                    <defs data-id="element-1646">
                      <linearGradient id="mhrGradient" x1="0" y1="0" x2="1" y2="0" data-id="element-1647">
                        <stop offset="0%" stopColor="#00D4D4" data-id="element-1648" />
                        <stop offset="100%" stopColor="#6B21E8" data-id="element-1649" />
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="time" hide data-id="element-1650" />
                    <YAxis hide domain={['dataMin - 10', 'dataMax + 10']} data-id="element-1651" />
                    <Tooltip contentStyle={{
                borderRadius: '12px',
                border: 'none',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
              }} formatter={(value: any) => [`${value} bpm`, 'Heart Rate']} data-id="element-1652" />
                    <Line type="monotone" dataKey="bpm" stroke="url(#mhrGradient)" strokeWidth={3} dot={{
                r: 3,
                fill: '#00D4D4'
              }} activeDot={{
                r: 5,
                fill: '#00D4D4'
              }} data-id="element-1653" />
                  </LineChart>
                </ResponsiveContainer>
              </div> : <div className="bg-secondary/40 rounded-[12px] p-3 text-center" data-id="element-1654">
                <p className="text-[13px] text-text-secondary" data-id="element-1655">
                  Log a few more readings to see your trend.
                </p>
              </div>}

            <p className="text-[11px] italic text-text-secondary leading-snug mt-4" data-id="element-1656">
              Resting maternal heart rate typically rises to 80–100 bpm in
              pregnancy. Persistent readings above 130 bpm or below 60 bpm
              should be discussed with your doctor.
            </p>
          </> : <div className="flex flex-col items-center justify-center text-center py-6" data-id="element-1657">
            <div className="w-14 h-14 rounded-full bg-accent/10 flex items-center justify-center mb-3" data-id="element-1658">
              <Heart size={24} className="text-accent" data-id="element-1659" />
            </div>
            <p className="text-[15px] font-medium text-text-primary mb-1" data-id="element-1660">
              No readings yet
            </p>
            <p className="text-[13px] text-text-secondary max-w-[260px] mb-4" data-id="element-1661">
              Track your heart rate over time to spot patterns in your
              pregnancy.
            </p>
            <Button onClick={() => setShowMhrSheet(true)} variant="secondary" className="py-2 px-4 h-auto text-[13px]" data-id="element-1662">
              Log Heart Rate
            </Button>
          </div>}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1663">
        {/* 4. WEIGHT TRACKER */}
        <div className="bg-white rounded-[24px] p-6 shadow-card border border-border/50 flex flex-col" data-id="element-1664">
          <div className="flex justify-between items-center mb-6" data-id="element-1665">
            <h2 className="text-[18px] font-semibold text-text-primary" data-id="element-1666">
              Weight
            </h2>
            <button onClick={() => setShowWeightSheet(true)} className="text-primary bg-primary/10 p-2 rounded-full hover:bg-primary/20 transition-colors" data-id="element-1667">
              <Plus size={18} data-id="element-1668" />
            </button>
          </div>

          {weightChartData.length > 0 ? <div className="h-[120px] w-full mt-auto" data-id="element-1669">
              <ResponsiveContainer width="100%" height="100%" data-id="element-1670">
                <LineChart data={weightChartData} data-id="element-1671">
                  <XAxis dataKey="date" hide data-id="element-1672" />
                  <YAxis hide domain={['dataMin - 2', 'dataMax + 2']} data-id="element-1673" />
                  <Tooltip contentStyle={{
                borderRadius: '12px',
                border: 'none',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
              }} data-id="element-1674" />
                  <Line type="monotone" dataKey="weight" stroke="#00D4D4" strokeWidth={3} dot={{
                r: 4,
                fill: '#00D4D4'
              }} data-id="element-1675" />
                </LineChart>
              </ResponsiveContainer>
            </div> : <div className="flex-1 flex flex-col items-center justify-center text-center py-6" data-id="element-1676">
              <p className="text-[14px] text-text-secondary mb-3" data-id="element-1677">
                No weight logged yet
              </p>
              <Button onClick={() => setShowWeightSheet(true)} variant="secondary" className="py-2 px-4 h-auto text-[13px]" data-id="element-1678">
                Log Weight
              </Button>
            </div>}
        </div>

        {/* 5. BLOOD PRESSURE LOG */}
        <div className="bg-white rounded-[24px] p-6 shadow-card border border-border/50 flex flex-col" data-id="element-1679">
          <div className="flex justify-between items-center mb-4" data-id="element-1680">
            <h2 className="text-[18px] font-semibold text-text-primary" data-id="element-1681">
              Blood Pressure
            </h2>
            <button onClick={() => setShowBpSheet(true)} className="text-primary bg-primary/10 p-2 rounded-full hover:bg-primary/20 transition-colors" data-id="element-1682">
              <Plus size={18} data-id="element-1683" />
            </button>
          </div>

          {bpLog.length > 0 ? <div className="space-y-3 overflow-y-auto max-h-[140px] custom-scrollbar pr-2" data-id="element-1684">
              {bpLog.slice(0, 5).map(entry => {
            const isHigh = entry.systolic > 140 || entry.diastolic > 90;
            return <div key={entry.id} className="flex justify-between items-center p-3 bg-secondary/50 rounded-[12px]" data-id="element-1685">
                    <div data-id="element-1686">
                      <div className="flex items-center" data-id="element-1687">
                        <span className="text-[16px] font-bold text-text-primary" data-id="element-1688">
                          {entry.systolic}/{entry.diastolic}
                        </span>
                        {isHigh && <span className="ml-2 w-2 h-2 rounded-full bg-danger" data-id="element-1689" />}
                      </div>
                      <span className="text-[12px] text-text-secondary" data-id="element-1690">
                        {new Date(entry.date).toLocaleDateString()}
                      </span>
                    </div>
                    {isHigh && <span className="text-[11px] font-semibold text-danger bg-danger/10 px-2 py-1 rounded-pill" data-id="element-1691">
                        High
                      </span>}
                  </div>;
          })}
            </div> : <div className="flex-1 flex flex-col items-center justify-center text-center py-6" data-id="element-1692">
              <p className="text-[14px] text-text-secondary mb-3" data-id="element-1693">
                No BP logged yet
              </p>
              <Button onClick={() => setShowBpSheet(true)} variant="secondary" className="py-2 px-4 h-auto text-[13px]" data-id="element-1694">
                Log BP
              </Button>
            </div>}
        </div>
      </div>

      {/* 6. SYMPTOM DIARY */}
      <div className="bg-white rounded-[24px] p-6 shadow-card border border-border/50" data-id="element-1695">
        <h2 className="text-[18px] font-semibold text-text-primary mb-4" data-id="element-1696">
          Symptom Diary
        </h2>
        <div className="flex flex-wrap gap-2 mb-6" data-id="element-1697">
          {SYMPTOMS.map(sym => <button key={sym} onClick={() => handleLogSymptom(sym)} className="px-4 py-2 bg-secondary text-text-primary text-[14px] font-medium rounded-pill hover:bg-primary/10 hover:text-primary transition-colors border border-transparent hover:border-primary/20" data-id="element-1698">
              + {sym}
            </button>)}
        </div>

        <div className="bg-secondary/50 rounded-[16px] p-4" data-id="element-1699">
          <h3 className="text-[13px] font-semibold text-text-secondary uppercase tracking-wider mb-3" data-id="element-1700">
            Today's Symptoms
          </h3>
          {todaySymptoms.length > 0 ? <div className="flex flex-wrap gap-2" data-id="element-1701">
              {todaySymptoms.map((s, i) => <div key={i} className="px-3 py-1.5 bg-white border border-border rounded-pill text-[13px] text-text-primary flex items-center shadow-sm" data-id="element-1702">
                  {s.symptom}
                  <span className="ml-2 text-[11px] text-text-secondary" data-id="element-1703">
                    {new Date(s.timestamp).toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit'
              })}
                  </span>
                </div>)}
            </div> : <p className="text-[14px] text-text-secondary" data-id="element-1704">
              No symptoms logged today.
            </p>}
        </div>
      </div>

      {/* 7. UPCOMING VISITS & 8. EMERGENCY CONTACT */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1705">
        <div className="bg-white rounded-[24px] p-6 shadow-card border border-border/50" data-id="element-1706">
          <div className="flex justify-between items-center mb-4" data-id="element-1707">
            <h2 className="text-[18px] font-semibold text-text-primary" data-id="element-1708">
              Upcoming Visit
            </h2>
            <button onClick={() => navigate('/app/appointments')} className="text-primary bg-primary/10 p-2 rounded-full hover:bg-primary/20 transition-colors" data-id="element-1709">
              <Plus size={18} data-id="element-1710" />
            </button>
          </div>

          {isLoading ? <SkeletonBlock height="80px" borderRadius="16px" data-id="element-1711" /> : nextAppointment ? <div className="bg-secondary/50 rounded-[16px] p-4" data-id="element-1712">
              <div className="flex items-start mb-2" data-id="element-1713">
                <Calendar size={18} className="text-primary mt-0.5 mr-2" data-id="element-1714" />
                <div data-id="element-1715">
                  <h3 className="text-[15px] font-semibold text-text-primary" data-id="element-1716">
                    {nextAppointment.title}
                  </h3>
                  <p className="text-[13px] text-text-secondary" data-id="element-1717">
                    {new Date(nextAppointment.appointmentDate).toLocaleDateString([], {
                  weekday: 'short',
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
                  </p>
                </div>
              </div>
              {nextAppointment.doctorName && <p className="text-[13px] text-text-primary ml-6 mt-1" data-id="element-1718">
                  Dr. {nextAppointment.doctorName}
                </p>}
            </div> : <div className="text-center py-4" data-id="element-1719">
              <p className="text-[14px] text-text-secondary mb-3" data-id="element-1720">
                No upcoming visits
              </p>
              <Button onClick={() => navigate('/app/appointments')} variant="secondary" className="py-2 px-4 h-auto text-[13px]" data-id="element-1721">
                Schedule Visit
              </Button>
            </div>}
        </div>

        <div className="bg-white rounded-[24px] p-6 shadow-card border border-border/50" data-id="element-1722">
          <h2 className="text-[18px] font-semibold text-text-primary mb-4" data-id="element-1723">
            Emergency Contact
          </h2>
          {nextAppointment?.doctorName ? <div className="bg-danger/5 border border-danger/10 rounded-[16px] p-4 flex items-center justify-between" data-id="element-1724">
              <div data-id="element-1725">
                <h3 className="text-[15px] font-semibold text-text-primary mb-1" data-id="element-1726">
                  Dr. {nextAppointment.doctorName}
                </h3>
                {nextAppointment.location && <p className="text-[13px] text-text-secondary" data-id="element-1727">
                    {nextAppointment.location}
                  </p>}
              </div>
              <a href="tel:911" className="w-10 h-10 bg-danger text-white rounded-full flex items-center justify-center shadow-md hover:bg-danger/90 transition-colors" data-id="element-1728">
                <Phone size={18} data-id="element-1729" />
              </a>
            </div> : <div className="text-center py-4" data-id="element-1730">
              <p className="text-[14px] text-text-secondary" data-id="element-1731">
                No emergency contact set — add one in Appointments
              </p>
            </div>}
        </div>
      </div>

      {/* BOTTOM SHEETS */}
      <AnimatePresence data-id="element-1732">
        {showWeightSheet && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setShowWeightSheet(false)} className="fixed inset-0 bg-black/40 z-[100]" data-id="element-1733" />
            <motion.div initial={{
          y: '100%'
        }} animate={{
          y: 0
        }} exit={{
          y: '100%'
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 200
        }} className="fixed bottom-0 left-0 right-0 bg-white rounded-t-[24px] p-6 z-[101] pb-[env(safe-area-inset-bottom)] md:max-w-[480px] md:mx-auto md:rounded-[24px] md:bottom-6 md:left-6 md:right-6" data-id="element-1734">
              <div className="flex justify-between items-center mb-6" data-id="element-1735">
                <h2 className="text-[20px] font-semibold text-text-primary" data-id="element-1736">
                  Log Weight
                </h2>
                <button onClick={() => setShowWeightSheet(false)} data-id="element-1737">
                  <X size={24} className="text-text-secondary" data-id="element-1738" />
                </button>
              </div>
              <div className="space-y-4 mb-6" data-id="element-1739">
                <div data-id="element-1740">
                  <label className="block text-[13px] font-medium text-text-primary mb-2" data-id="element-1741">
                    Weight (kg)
                  </label>
                  <InputField type="number" placeholder="e.g. 65" value={weightInput} onChange={e => setWeightInput(e.target.value)} data-id="element-1742" />
                </div>
                <div data-id="element-1743">
                  <label className="block text-[13px] font-medium text-text-primary mb-2" data-id="element-1744">
                    Date
                  </label>
                  <input type="date" value={weightDate} onChange={e => setWeightDate(e.target.value)} className="w-full h-[52px] rounded-[12px] border border-border px-4 focus:border-primary outline-none" data-id="element-1745" />
                </div>
              </div>
              <Button onClick={handleSaveWeight} disabled={!weightInput} data-id="element-1746">
                Save Entry
              </Button>
            </motion.div>
          </>}

        {showBpSheet && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setShowBpSheet(false)} className="fixed inset-0 bg-black/40 z-[100]" data-id="element-1747" />
            <motion.div initial={{
          y: '100%'
        }} animate={{
          y: 0
        }} exit={{
          y: '100%'
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 200
        }} className="fixed bottom-0 left-0 right-0 bg-white rounded-t-[24px] p-6 z-[101] pb-[env(safe-area-inset-bottom)] md:max-w-[480px] md:mx-auto md:rounded-[24px] md:bottom-6 md:left-6 md:right-6" data-id="element-1748">
              <div className="flex justify-between items-center mb-6" data-id="element-1749">
                <h2 className="text-[20px] font-semibold text-text-primary" data-id="element-1750">
                  Log Blood Pressure
                </h2>
                <button onClick={() => setShowBpSheet(false)} data-id="element-1751">
                  <X size={24} className="text-text-secondary" data-id="element-1752" />
                </button>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-4" data-id="element-1753">
                <div data-id="element-1754">
                  <label className="block text-[13px] font-medium text-text-primary mb-2" data-id="element-1755">
                    Systolic
                  </label>
                  <InputField type="number" placeholder="120" value={bpSysInput} onChange={e => setBpSysInput(e.target.value)} data-id="element-1756" />
                </div>
                <div data-id="element-1757">
                  <label className="block text-[13px] font-medium text-text-primary mb-2" data-id="element-1758">
                    Diastolic
                  </label>
                  <InputField type="number" placeholder="80" value={bpDiaInput} onChange={e => setBpDiaInput(e.target.value)} data-id="element-1759" />
                </div>
              </div>
              <div className="mb-6" data-id="element-1760">
                <label className="block text-[13px] font-medium text-text-primary mb-2" data-id="element-1761">
                  Date
                </label>
                <input type="date" value={bpDate} onChange={e => setBpDate(e.target.value)} className="w-full h-[52px] rounded-[12px] border border-border px-4 focus:border-primary outline-none" data-id="element-1762" />
              </div>
              <Button onClick={handleSaveBp} disabled={!bpSysInput || !bpDiaInput} data-id="element-1763">
                Save Entry
              </Button>
            </motion.div>
          </>}

        {showMhrSheet && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setShowMhrSheet(false)} className="fixed inset-0 bg-black/40 z-[100]" data-id="element-1764" />
            <motion.div initial={{
          y: '100%'
        }} animate={{
          y: 0
        }} exit={{
          y: '100%'
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 200
        }} className="fixed bottom-0 left-0 right-0 bg-white rounded-t-[24px] p-6 z-[101] pb-[env(safe-area-inset-bottom)] md:max-w-[480px] md:mx-auto md:rounded-[24px] md:bottom-6 md:left-6 md:right-6" data-id="element-1765">
              <div className="flex justify-between items-center mb-6" data-id="element-1766">
                <h2 className="text-[20px] font-semibold text-text-primary" data-id="element-1767">
                  Log Heart Rate
                </h2>
                <button onClick={() => setShowMhrSheet(false)} data-id="element-1768">
                  <X size={24} className="text-text-secondary" data-id="element-1769" />
                </button>
              </div>
              <div className="space-y-4 mb-6" data-id="element-1770">
                <div data-id="element-1771">
                  <label className="block text-[13px] font-medium text-text-primary mb-2" data-id="element-1772">
                    Heart Rate (bpm)
                  </label>
                  <InputField type="number" placeholder="e.g. 82" value={mhrInput} onChange={e => setMhrInput(e.target.value)} data-id="element-1773" />
                </div>
                <div className="grid grid-cols-2 gap-4" data-id="element-1774">
                  <div data-id="element-1775">
                    <label className="block text-[13px] font-medium text-text-primary mb-2" data-id="element-1776">
                      Date
                    </label>
                    <input type="date" value={mhrDate} onChange={e => setMhrDate(e.target.value)} className="w-full h-[52px] rounded-[12px] border border-border px-4 focus:border-primary outline-none" data-id="element-1777" />
                  </div>
                  <div data-id="element-1778">
                    <label className="block text-[13px] font-medium text-text-primary mb-2" data-id="element-1779">
                      Time
                    </label>
                    <input type="time" value={mhrTime} onChange={e => setMhrTime(e.target.value)} className="w-full h-[52px] rounded-[12px] border border-border px-4 focus:border-primary outline-none" data-id="element-1780" />
                  </div>
                </div>
              </div>
              <Button onClick={handleSaveMhr} disabled={!mhrInput} data-id="element-1781">
                Save Entry
              </Button>
            </motion.div>
          </>}
      </AnimatePresence>
    </main>;
}